/** One row: what it commits, what it reads as, and an aside on the right. */
export type SuggestionComboboxOption = {
  value: string
  label: string
  /** Shown muted at the end of the row — a type, a status, a count. */
  hint?: string
  /** What the search matches against. Defaults to the label and the hint. */
  searchText?: string
}

/** The row pinned above the rest, and the grounds shown under it. */
export type ComboboxSuggestion = {
  value: string
  /** Why. Rendered with the row, because a suggestion nobody can judge is noise. */
  reason: string
}

/**
 * The rows listed under the suggestion: everything else, filtered by the query.
 *
 * The suggested row is drawn once, pinned above these — left in here as well it
 * reads as two rows for one thing. Pinning is also why the search is applied
 * here rather than by the combobox: Reka's own filter would drop the suggestion
 * the moment the query stopped matching it, which is exactly when a suggestion
 * is worth keeping in view.
 *
 * `contains` is passed in rather than imported so this stays pure — the
 * component hands it Reka's locale-aware matcher, and a test can hand it
 * anything.
 */
export const searchOptions = (
  options: SuggestionComboboxOption[],
  query: string,
  contains: (haystack: string, needle: string) => boolean,
  suggestedValue?: string,
): SuggestionComboboxOption[] => {
  const rest = options.filter(option => option.value !== suggestedValue)

  const search = query.trim()
  if (!search) return rest

  return rest.filter(option =>
    contains(option.searchText ?? `${option.label} ${option.hint ?? ''}`, search),
  )
}
